// @flow
import React, { useState, useCallback, useEffect } from 'react';
import type { Node } from 'react';
import { get, camelCase } from 'lodash';
import moment from 'moment';
import { PlatformSwitch, SettingsMenu, AppMobileFooter, AppMobileHeader, FormFooter, BlockedFeature } from '@blocks';
import { Breadcrumb, Input, SelectBox, RadioGroup, InputMask, Button, Checkbox, Spinner, Icon } from '@elements';
import type { User, BundledPermissions } from '@types';
import { useTranslationNs, fSufixTwoPoints, isPresent } from '@helpers/helpers';
import { brStates } from '@helpers/constants';
import { isValidCpf } from '@helpers/validator/rules/testers';
import './MemedSettings.scss';

export type MemedAccount = {
  connected: boolean,
  board: 'CRM' | 'CRO' | 'COREN',
  name: ?string,
  cpf: ?string,
  birthDate: ?string,
  councilDoc: ?string,
  councilState: ?string,
  gender: ?string,
};

export type MemedForm = {
  name: string,
  cpf: string,
  birthDate: string,
  councilDoc: string,
  councilState: string,
  gender: string,
};

export type MemedLoadError = { status: ?number, message: string };

type Props = {
  user: User,
  permissions: BundledPermissions,
  onLogout: Function,
  account: ?MemedAccount,
  isLoading: boolean,
  isSaving: boolean,
  loadError: ?MemedLoadError,
  error: ?string,
  fieldErrors: { [field: string]: string },
  onReload: Function,
  onSubmit: (form: MemedForm) => void,
  onBack: ?Function,
};

const formFromAccount = (account: MemedAccount): MemedForm => {
  const birthDate = moment(get(account, 'birthDate', ''), 'YYYY-MM-DD', true);
  return {
    name: get(account, 'name') || '',
    cpf: get(account, 'cpf') || '',
    birthDate: birthDate.isValid() ? birthDate.format('DD/MM/YYYY') : '',
    councilDoc: get(account, 'councilDoc') || '',
    councilState: get(account, 'councilState') || '',
    gender: get(account, 'gender') || '',
  };
};

type FormProps = {
  account: MemedAccount,
  isSaving: boolean,
  error: ?string,
  fieldErrors: { [field: string]: string },
  onSubmit: (form: MemedForm) => void,
  backButton: Node,
  support: Node,
};

// Mounted once the account has loaded: the council's state and the gender only read their value then
const AccountForm = ({
  account, isSaving, error, fieldErrors, onSubmit, backButton, support,
}: FormProps) => {
  const [_, i18n] = useTranslationNs('settings');
  const [form, setForm] = useState(() => formFromAccount(account));
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const board = get(account, 'board') || 'CRM';
  // `_` has no interpolation: texts that name the council go through i18n.t
  const withBoard = (key: string) => String(i18n.t(`settings:${key}`, { board }));

  // The API's own checks, under the fields they belong to
  useEffect(() => {
    const fromApi = {};
    // The API names fields after its params (birth_date); the form after the app's (birthDate)
    Object.keys(fieldErrors || {}).forEach((field) => {
      fromApi[camelCase(field)] = fieldErrors[field];
    });
    setErrors(fromApi);
  }, [fieldErrors]);

  const handleChange = useCallback(({ name, value }) => {
    setForm(current => ({ ...current, [name]: value }));
  }, []);

  const handleConsent = useCallback(({ value }) => setConsent(value), []);

  const handleSave = useCallback(() => {
    const required = _('msg/required_field', 'global');
    const found = {};
    if (!isPresent(form.name)) found.name = required;
    else if (form.name.trim().split(/\s+/).length < 2) found.name = _('memed/error/name');
    if (!isPresent(form.cpf)) found.cpf = required;
    else if (!isValidCpf.run(form.cpf, { isSubmit: true })) found.cpf = _('msg/invalid_cpf', 'global');
    if (!isPresent(form.birthDate)) found.birthDate = required;
    else if (!moment(form.birthDate, 'DD/MM/YYYY', true).isValid()) found.birthDate = _('memed/error/birth_date');
    if (!isPresent(form.councilDoc)) found.councilDoc = required;
    else if (!/^\d+$/.test(String(form.councilDoc).trim())) found.councilDoc = _('memed/error/council_doc');
    if (!isPresent(form.councilState)) found.councilState = required;
    if (!isPresent(form.gender)) found.gender = required;
    if (!consent) found.consent = _('memed/error/consent');

    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit({ ...form, councilDoc: String(form.councilDoc).trim() });
  }, [_, consent, form, onSubmit]);

  return (
    <React.Fragment>
      <p className="memedSettings-intro">
        {board === 'CRM' ? _('memed/intro') : withBoard('memed/intro_other_board')}
      </p>
      {error && <p className="memedSettings-error" role="alert">{error}</p>}
      <div className="memedSettings-form">
        <Input
          required
          type="text"
          name="name"
          label={_('memed/form/name', null, fSufixTwoPoints)}
          value={form.name}
          onChange={handleChange}
          error={get(errors, 'name')}
          disabled={isSaving}
        />
        <InputMask
          required
          type="cpf"
          name="cpf"
          label={_('memed/form/cpf', null, fSufixTwoPoints)}
          hint={_('memed/form/cpf_hint')}
          value={form.cpf}
          onChange={handleChange}
          error={get(errors, 'cpf')}
          disabled={isSaving}
        />
        <InputMask
          required
          type="date"
          name="birthDate"
          label={_('memed/form/birth_date', null, fSufixTwoPoints)}
          value={form.birthDate}
          onChange={handleChange}
          error={get(errors, 'birthDate')}
          disabled={isSaving}
        />
        <Input
          required
          type="text"
          name="councilDoc"
          label={fSufixTwoPoints(withBoard('memed/form/council_doc'))}
          value={form.councilDoc}
          onChange={handleChange}
          error={get(errors, 'councilDoc')}
          disabled={isSaving}
        />
        <SelectBox
          required
          name="councilState"
          label={fSufixTwoPoints(withBoard('memed/form/council_state'))}
          options={brStates}
          value={form.councilState}
          onChange={handleChange}
          error={get(errors, 'councilState')}
          disabled={isSaving}
        />
        <RadioGroup
          uncontrolled
          label={_('memed/form/gender', null, fSufixTwoPoints)}
          name="gender"
          defaultType="gender"
          selectGroupName="gender"
          defaultValue={form.gender}
          onChange={handleChange}
          disabled={isSaving}
        />
        {get(errors, 'gender') && <p className="memedSettings-fieldError">{get(errors, 'gender')}</p>}
        <Checkbox
          name="consent"
          className="memedSettings-consent"
          label={_('memed/form/consent')}
          isChecked={consent}
          onChange={handleConsent}
          disabled={isSaving}
        />
        {get(errors, 'consent') && <p className="memedSettings-fieldError">{get(errors, 'consent')}</p>}
      </div>
      <FormFooter
        onSave={handleSave}
        isLoading={isSaving}
        saveText={_('memed/form/save')}
        savingText={board === 'CRM' ? _('memed/form/saving') : _('memed/form/saving_other_board')}
      />
      {backButton}
      {support}
    </React.Fragment>
  );
};

const MemedSettings = ({
  user, permissions, onLogout, account, isLoading, isSaving, loadError, error, fieldErrors, onReload, onSubmit, onBack,
}: Props) => {
  const [_, i18n] = useTranslationNs('settings');
  const board = get(account, 'board') || 'CRM';
  const planIncludesMemed = get(permissions, ['planInclude', 'newMemed'], false);

  const support = (
    <div className="memedSettings-support">
      <Icon iconName="support" size={18} />
      <span>{_('memed/support')}</span>
      <Button intercom className="memedSettings-supportButton">{_('memed/support/button')}</Button>
    </div>
  );

  const backButton = (primary: boolean) => onBack && (
    <Button className={primary ? 'primary' : 'memedSettings-back'} onClick={onBack}>{_('memed/connected/back')}</Button>
  );

  const connectedContent = () => {
    // Who is connected. Not the council number: for accounts connected before 2023 the API's
    // councilDoc and councilState can be the profile's free text and the home address's state.
    const accountName = get(account, 'name');

    return (
      <div className="memedSettings-status">
        <div className="memedSettings-card">
          <div className="memedSettings-cardHeader">
            <span className="memedSettings-statusIcon"><Icon iconName="check-circle" size={26} /></span>
            <div className="memedSettings-cardTitle">
              <h2>{_('memed/connected/title')}</h2>
              {accountName && <p className="memedSettings-account">{accountName}</p>}
            </div>
          </div>
          <p className="memedSettings-howTo">{_('memed/connected/text')}</p>
          {board !== 'CRM' && (
            <p className="memedSettings-notice">
              <Icon iconName="info" size={18} />
              <span>{String(i18n.t('settings:memed/connected/review', { board }))}</span>
            </p>
          )}
          {onBack && <div className="memedSettings-cardActions">{backButton(true)}</div>}
        </div>
        {support}
      </div>
    );
  };

  const content = () => {
    if (!planIncludesMemed) return <BlockedFeature simpleStyle iconName="lock" />;
    if (isLoading) return <div className="memedSettings-loading"><Spinner /></div>;
    if (loadError || !account) {
      // A 403 is the API saying Memed isn't for this account: its reason, and nothing to retry
      const forbidden = get(loadError, 'status') === 403;
      return (
        <div className="memedSettings-status">
          <p className="memedSettings-error" role="alert">{forbidden ? get(loadError, 'message') : _('memed/error/load')}</p>
          {!forbidden && <Button onClick={onReload}>{_('memed/error/retry')}</Button>}
          {support}
        </div>
      );
    }
    if (account.connected) return connectedContent();
    return (
      <AccountForm
        account={account}
        isSaving={isSaving}
        error={error}
        fieldErrors={fieldErrors}
        onSubmit={onSubmit}
        backButton={backButton(false)}
        support={support}
      />
    );
  };

  const breadcrumbLinks = [
    { text: _('label/prescription_menu/title'), linkTo: '/ajustes/modelos' },
    { text: _('memed/title') },
  ];

  return (
    <PlatformSwitch
      inject
      desktop={() => (
        <div className="appContent settingsPage memedSettings">
          <aside className="sidebar">
            <SettingsMenu user={user} onLogout={onLogout} permissions={permissions} />
          </aside>
          <section className="mainSection">
            <div className="centerContent">
              <Breadcrumb items={breadcrumbLinks} />
              <div className="toolbar dashed">
                <h1>{_('memed/title')}</h1>
              </div>
              {content()}
            </div>
          </section>
        </div>
      )}
      mobile={() => (
        <React.Fragment>
          <AppMobileHeader title={_('memed/title')} backLinkTo="/ajustes/modelos" onBackClick={onBack || undefined} />
          <div className="appContent settingsPage memedSettings">
            <section className="mainSection">
              <div className="centerContent">
                {content()}
              </div>
            </section>
          </div>
          <AppMobileFooter />
        </React.Fragment>
      )}
    />
  );
};

export default MemedSettings;
